import React, { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Full-screen success overlay with a scaling green check + subtle radial
 * "fireworks" of dots + soft grid glow. Designed to sit briefly on top of
 * any dark screen (order confirmation, payment success, etc.).
 */
export default function SuccessOverlay({
  title = "Order placed",
  subtitle = "Redirecting to your order…",
  onDone,
  autoCloseMs = 2600,
}) {
  React.useEffect(() => {
    if (!onDone) return;
    const t = setTimeout(onDone, autoCloseMs);
    return () => clearTimeout(t);
  }, [onDone, autoCloseMs]);

  // Pre-computed firework particles — radial, deterministic (no re-render jitter)
  const particles = useMemo(() => {
    const N = 22;
    return Array.from({ length: N }).map((_, i) => {
      const angle = (i / N) * Math.PI * 2;
      const r = 160 + Math.random() * 60;
      const dx = Math.cos(angle) * r;
      const dy = Math.sin(angle) * r;
      const color = i % 5 === 0 ? "#22c55e" : i % 3 === 0 ? "#6589ff" : "#ffffff";
      const size = 3 + Math.random() * 3;
      const delay = 0.15 + (i % 6) * 0.03;
      return { dx, dy, color, size, delay };
    });
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-[300] flex items-center justify-center overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      data-testid="success-overlay"
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at center, rgba(34,197,94,0.14) 0%, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0.95) 100%)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        }}
      />

      {/* Firework particles */}
      {particles.map((p, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: "50%",
            top: "50%",
            width: p.size,
            height: p.size,
            background: p.color,
            boxShadow: `0 0 8px ${p.color}80`,
          }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
          animate={{ x: p.dx, y: p.dy, opacity: [0, 1, 0], scale: [0, 1.2, 0.6] }}
          transition={{
            duration: 1.2,
            delay: p.delay,
            ease: [0.22, 1, 0.36, 1],
          }}
        />
      ))}

      {/* Concentric rings */}
      {[0, 1, 2].map((i) => (
        <motion.span
          key={`ring-${i}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            border: "1px solid rgba(34,197,94,0.35)",
          }}
          initial={{ width: 96, height: 96, opacity: 0 }}
          animate={{ width: 320, height: 320, opacity: [0, 0.5, 0] }}
          transition={{
            duration: 1.8,
            delay: 0.15 + i * 0.28,
            ease: [0.22, 1, 0.36, 1],
          }}
        />
      ))}

      {/* Central check circle */}
      <motion.div
        className="relative flex flex-col items-center gap-6 z-10"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
      >
        <div
          className="relative h-24 w-24 rounded-full flex items-center justify-center"
          style={{
            background:
              "linear-gradient(180deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.06) 100%)",
            border: "1.5px solid rgba(34,197,94,0.6)",
            boxShadow:
              "0 0 0 6px rgba(34,197,94,0.10), 0 20px 40px -12px rgba(34,197,94,0.35)",
          }}
        >
          {/* pulsing inner glow */}
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{ background: "rgba(34,197,94,0.20)" }}
            animate={{ opacity: [0.4, 0.15, 0.4], scale: [1, 1.08, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
          <svg
            width="42"
            height="42"
            viewBox="0 0 42 42"
            fill="none"
            stroke="#22c55e"
            strokeWidth="3.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <motion.path
              d="M9 22 L18 30 L33 13"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.55, delay: 0.35, ease: [0.65, 0, 0.35, 1] }}
            />
          </svg>
        </div>

        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.35 }}
        >
          <div
            className="text-xs uppercase tracking-[0.32em] mb-2"
            style={{ color: "#22c55e" }}
          >
            Confirmed
          </div>
          <div className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
            {title}
          </div>
          <div className="mt-3 text-sm text-[#a0a0a0]">{subtitle}</div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
